import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, AlertTriangle, CheckCircle2, ArrowLeft, ArrowRight, CreditCard } from "lucide-react";
import { useLang } from "@/i18n";
import api, { errMsg, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY_LOT = { lot_number: "", url: "", year: "", make: "", model: "", mileage: "", title_type: "", location: "", sale_date: "", image_url: "", fetch_status: "manual" };

export default function NewBid() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const [lot, setLot] = useState(EMPTY_LOT);
  const [maxBid, setMaxBid] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const deposit = useMemo(() => {
    const mb = Number(maxBid) || 0;
    const pct = settings?.deposit_pct ?? 10;
    const min = settings?.min_deposit ?? 400;
    return Math.max((mb * pct) / 100, min);
  }, [maxBid, settings]);
  const fee = settings?.flat_fee ?? 1000;

  const doFetch = async () => {
    if (!query.trim()) return;
    setFetching(true);
    setFetchResult(null);
    try {
      const { data } = await api.post("/lots/fetch", { query });
      setFetchResult(data);
      setLot((prev) => ({
        ...prev,
        lot_number: data.lot_number || prev.lot_number,
        url: data.url || (query.startsWith("http") ? query : prev.url),
        year: data.year || prev.year,
        make: data.make || prev.make,
        model: data.model || prev.model,
        image_url: data.image_url || prev.image_url,
        fetch_status: data.fetched ? "auto" : "manual",
      }));
      if (data.fetched) toast.success(t("Datos del lote cargados. Revísalos antes de continuar."));
      else toast.info(t("No pudimos obtener los datos automáticamente (Copart bloquea el acceso). Complétalos manualmente abajo."));
    } catch (e) {
      setFetchResult({ fetched: false });
      toast.info(t("Búsqueda automática no disponible. Ingresa los datos manualmente."));
    } finally {
      setFetching(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: bid } = await api.post("/bid-requests", {
        lot, max_bid: Number(maxBid), accepted_terms: accepted,
      });
      toast.success(t("Solicitud creada. Ahora paga tu depósito reembolsable."));
      const { data: checkout } = await api.post("/payments/deposit/checkout", {
        bid_request_id: bid.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setSubmitting(false);
    }
  };

  const lotField = (key, label, placeholder, testid, type = "text") => (
    <div>
      <Label className="text-slate-300 text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={lot[key]} placeholder={placeholder}
        onChange={(e) => setLot({ ...lot, [key]: e.target.value })}
        className="mt-1.5 bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12" data-testid="new-bid-page">
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t("Nueva solicitud de puja")}</h1>
      <p className="text-slate-400 text-sm mb-10">{t("Paso {step} de 3", { step })} — {step === 1 ? t("Datos del lote") : step === 2 ? t("Puja máxima y depósito") : t("Confirmación")}</p>

      <div className="flex gap-2 mb-10">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${s <= step ? "bg-[#2563EB]" : "bg-slate-700"}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-6 fade-up">
          <div className="card-tech rounded-xl p-6">
            <Label className="text-slate-300">{t("URL o número de lote de Copart")}</Label>
            <div className="flex gap-3 mt-2">
              <Input data-testid="lot-query" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="https://www.copart.com/lot/12345678 o 12345678"
                className="bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500" />
              <Button data-testid="lot-fetch-btn" onClick={doFetch} disabled={fetching}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white shrink-0">
                <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />{fetching ? t("Buscando...") : t("Buscar")}
              </Button>
            </div>
            {fetchResult && !fetchResult.fetched && (
              <div data-testid="fetch-fallback-notice" className="mt-4 flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>{t("Búsqueda automática no disponible — esto es normal, Copart bloquea el acceso automatizado. Completa los datos manualmente; nuestro equipo los verificará.")}</span>
              </div>
            )}
            {fetchResult?.fetched && (
              <div data-testid="fetch-success-notice" className="mt-4 flex items-center gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2.5">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> {t("Datos cargados automáticamente. Verifícalos abajo.")}
              </div>
            )}
          </div>

          <div className="card-tech rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-5">{t("Datos del vehículo")}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {lotField("lot_number", t("Número de lote"), "Ej. 87654321", "lot-number")}
              {lotField("sale_date", t("Fecha de subasta"), "", "lot-sale-date", "date")}
              {lotField("year", t("Año"), "Ej. 2021", "lot-year")}
              {lotField("make", t("Marca"), "Ej. Toyota", "lot-make")}
              {lotField("model", t("Modelo"), "Ej. Corolla", "lot-model")}
              {lotField("mileage", t("Millaje"), "Ej. 45,000 mi", "lot-mileage")}
              <div>
                <Label className="text-slate-300 text-xs">{t("Tipo de título")}</Label>
                <Select value={lot.title_type} onValueChange={(v) => setLot({ ...lot, title_type: v })}>
                  <SelectTrigger data-testid="lot-title-type" className="mt-1.5 bg-[#0B0F1A] border-white/15"><SelectValue placeholder={t("Seleccionar")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Clean">Clean</SelectItem>
                    <SelectItem value="Salvage">Salvage</SelectItem>
                    <SelectItem value="Rebuilt">Rebuilt</SelectItem>
                    <SelectItem value="Otro">{t("Otro / No sé")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {lotField("location", t("Ubicación (patio Copart)"), "Ej. Miami, FL", "lot-location")}
              <div className="sm:col-span-2">{lotField("image_url", t("URL de imagen (opcional)"), "https://...", "lot-image")}</div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button data-testid="step1-next" onClick={() => setStep(2)}
              disabled={!lot.lot_number && !(lot.year && lot.make)}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8">
              {t("Continuar")} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6 fade-up">
          <div className="card-tech tracing-beam rounded-xl p-8">
            <Label className="text-slate-300">{t("Puja máxima autorizada (USD)")}</Label>
            <Input data-testid="max-bid-input" type="number" min="1" value={maxBid} onChange={(e) => setMaxBid(e.target.value)}
              placeholder="Ej. 8000" className="mt-2 bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500 text-lg h-12" />
            <p className="text-xs text-slate-500 mt-2">{t("Nuestro equipo nunca pujará por encima de este monto.")}</p>

            <div className="mt-8 space-y-3 text-sm border-t border-white/10 pt-6">
              <div className="flex justify-between text-slate-300">
                <span>{t("Depósito reembolsable ({pct}% · mín. {min})", { pct: settings?.deposit_pct ?? 10, min: fmtUSD(settings?.min_deposit ?? 400) })}</span>
                <span data-testid="deposit-amount" className="font-bold text-[#2563EB]">{fmtUSD(deposit)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>{t("Comisión de bróker (solo si ganamos)")}</span>
                <span data-testid="broker-fee" className="font-semibold">{fmtUSD(fee)}</span>
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed pt-2">
                {t("Si no ganamos la subasta, tu depósito se reembolsa automáticamente menos la cuota de procesamiento de tarjeta ({us}% tarjetas de EE.UU. / {foreign}% tarjetas extranjeras). Si ganamos y no pagas el saldo en el plazo indicado, el depósito puede ser retenido como penalidad.", { us: settings?.refund_fee_us ?? 3.5, foreign: settings?.refund_fee_foreign ?? 4.5 })}
              </div>
            </div>

            <div className="flex items-start gap-3 mt-6">
              <Checkbox data-testid="accept-terms" id="terms" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} />
              <label htmlFor="terms" className="text-xs text-slate-300 leading-relaxed cursor-pointer">
                {t("Acepto la comisión de bróker de {fee}, la política de reembolso del depósito y autorizo a CarBid a pujar hasta mi monto máximo en su cuenta de Copart.", { fee: fmtUSD(fee) })}
              </label>
            </div>
          </div>

          <div className="flex justify-between">
            <Button data-testid="step2-back" variant="outline" onClick={() => setStep(1)}
              className="border-white/15 bg-transparent hover:bg-white/5 hover:text-white rounded-full px-6">
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("Atrás")}
            </Button>
            <Button data-testid="step2-next" onClick={() => setStep(3)} disabled={!Number(maxBid) || !accepted}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8">
              {t("Revisar")} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6 fade-up">
          <div className="card-tech rounded-xl p-8">
            <h3 className="text-lg font-semibold mb-6">{t("Resumen de tu solicitud")}</h3>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">{t("Vehículo")}</span><span data-testid="summary-vehicle" className="font-medium">{`${lot.year} ${lot.make} ${lot.model}`.trim() || "—"}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">{t("Lote")}</span><span data-testid="summary-lot" className="font-medium">#{lot.lot_number || "—"}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">{t("Fecha de subasta")}</span><span className="font-medium">{lot.sale_date || t("Por confirmar")}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">{t("Puja máxima")}</span><span data-testid="summary-max-bid" className="font-medium">{fmtUSD(Number(maxBid))}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">{t("Comisión (si ganamos)")}</span><span className="font-medium">{fmtUSD(fee)}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">{t("Depósito a pagar ahora")}</span><span data-testid="summary-deposit" className="font-bold text-[#22C55E]">{fmtUSD(deposit)}</span></div>
            </div>
          </div>
          <div className="flex justify-between">
            <Button data-testid="step3-back" variant="outline" onClick={() => setStep(2)}
              className="border-white/15 bg-transparent hover:bg-white/5 hover:text-white rounded-full px-6">
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("Atrás")}
            </Button>
            <Button data-testid="pay-deposit-btn" onClick={submit} disabled={submitting}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-full px-8">
              <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {submitting ? t("Procesando...") : t("Pagar depósito {amount}", { amount: fmtUSD(deposit) })}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 text-center">{t("Pago seguro procesado por Stripe. El depósito es reembolsable si no ganamos tu subasta.")}</p>
        </div>
      )}
    </div>
  );
}
